import { VesselCardChartPoint, VesselCardChartRow } from "../models/vessel-card.models";
import { getVesselCardExpectedCurve } from "../utils/vessel-card-chart.utils";

const getComparisonPoints = (rows: VesselCardChartRow[]): VesselCardChartPoint[] =>
  rows
    .flatMap((row) => {
      const speed = row.comparisonSpeed;
      const consumption = row.comparisonConsumption;
      return typeof speed === "number" &&
        Number.isFinite(speed) &&
        speed > 0 &&
        typeof consumption === "number" &&
        Number.isFinite(consumption) &&
        consumption >= 0
        ? [{ speed, consumption, mode: row.mode }]
        : [];
    })
    .sort((left, right) => left.speed - right.speed);

const getAxis = (values: number[], minimumStep: number) => {
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const roughStep = Math.max(minimumStep, (maximum - minimum) / 5);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const step = [1, 2, 5, 10].map((factor) => factor * magnitude).find((value) => value >= roughStep)!;
  const start = Math.floor((minimum - step / 2) / step) * step;
  const end = Math.ceil((maximum + step / 2) / step) * step;
  const ticks = Array.from({ length: Math.round((end - start) / step) + 1 }, (_, index) => start + index * step);
  return { start, end, ticks };
};

// The report uses numeric speed/consumption axes, with separate Ballast and Laden series.
// Expected curves follow Graph View Calcs.xlsm, including its rounded, pinned endpoints.
const VesselCardPerformanceChart = ({
  condition,
  rows,
  comparisonLabel,
  comparisonKey,
}: {
  condition: "Ballast" | "Laden";
  rows: VesselCardChartRow[];
  comparisonLabel: string;
  comparisonKey: "cp" | "baltic";
}) => {
  const conditionRows = rows.filter((row) => row.mode.toLowerCase().startsWith(condition.toLowerCase()));
  const curve = getVesselCardExpectedCurve(conditionRows);
  const expected = curve.endpoints;
  const comparison = getComparisonPoints(conditionRows);
  const points = [...curve.points, ...expected, ...comparison];
  const xAxis = points.length
    ? getAxis(
        points.map((point) => point.speed),
        0.5,
      )
    : null;
  const yAxis = points.length
    ? getAxis(
        points.map((point) => point.consumption),
        2,
      )
    : null;
  const x = (speed: number) => 54 + ((speed - xAxis!.start) / (xAxis!.end - xAxis!.start)) * 352;
  const y = (consumption: number) => 206 - ((consumption - yAxis!.start) / (yAxis!.end - yAxis!.start)) * 158;

  return (
    <article className="min-w-0" data-test={`vessel-card-${condition.toLowerCase()}-chart`}>
      <h3 className="text-center text-sm font-semibold text-gray-900 dark:text-gray-100">{condition}</h3>
      <div className="mt-2 flex justify-center gap-3 text-xs text-gray-600 dark:text-gray-300">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-teal-600 dark:bg-teal-300" />
          Expected Performance
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-purple-600 dark:bg-purple-400" />
          {comparisonLabel}
        </span>
      </div>
      {xAxis && yAxis ? (
        <svg
          viewBox="0 0 440 248"
          role="img"
          aria-label={`${condition} speed versus consumption: Expected Performance and ${comparisonLabel}`}
          className="w-full text-gray-500 dark:text-gray-400"
        >
          <desc>
            Speed in knots and consumption in tonnes per day. Expected curves use the workbook's 2.8 power law, 2 t/day
            auxiliary allowance and explicit endpoint values. Missing values are omitted.
          </desc>
          {xAxis.ticks.map((tick) => (
            <g key={`x-${tick}`}>
              <line
                x1={x(tick)}
                x2={x(tick)}
                y1={48}
                y2={206}
                stroke="currentColor"
                opacity={0.3}
                strokeDasharray="1 4"
              />
              <text x={x(tick)} y={222} textAnchor="middle" fill="currentColor" fontSize={10}>
                {tick.toFixed(1)}
              </text>
            </g>
          ))}
          {yAxis.ticks.map((tick) => (
            <g key={`y-${tick}`}>
              <line
                x1={54}
                x2={406}
                y1={y(tick)}
                y2={y(tick)}
                stroke="currentColor"
                opacity={0.3}
                strokeDasharray="1 4"
              />
              <text x={44} y={y(tick) + 3} textAnchor="end" fill="currentColor" fontSize={10}>
                {tick}
              </text>
            </g>
          ))}
          <text x={230} y={241} textAnchor="middle" fill="currentColor" fontSize={11}>
            Speed (kn)
          </text>
          <text transform="translate(15 127) rotate(-90)" textAnchor="middle" fill="currentColor" fontSize={11}>
            Consumption (t/day)
          </text>
          {[
            {
              name: "Expected Performance",
              key: "expected",
              points: expected,
              linePoints: curve.points,
              colorClassName: "text-teal-600 dark:text-teal-300",
            },
            {
              name: comparisonLabel,
              key: comparisonKey,
              points: comparison,
              linePoints: comparison,
              colorClassName: "text-purple-600 dark:text-purple-400",
            },
          ].map((series) => (
            <g
              key={series.name}
              className={series.colorClassName}
              data-test={`vessel-card-${condition.toLowerCase()}-${series.key}-series`}
            >
              <polyline
                points={series.linePoints.map((point) => `${x(point.speed)},${y(point.consumption)}`).join(" ")}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeDasharray={series.key === "expected" ? undefined : "6 4"}
              />
              {series.points.map((point, index) => (
                <g key={`${point.mode}-${index}`}>
                  <circle cx={x(point.speed)} cy={y(point.consumption)} r={2.5} fill="currentColor">
                    <title>{`${series.name} ${point.mode}: ${point.speed.toFixed(1)} kn, ${point.consumption.toFixed(1)} t/day`}</title>
                  </circle>
                  <text
                    x={x(point.speed)}
                    y={y(point.consumption) + (series.key === "expected" ? -9 : 16)}
                    textAnchor={index === 0 ? "start" : "end"}
                    fill="currentColor"
                    fontSize={11}
                  >
                    {point.speed.toFixed(1)} / {point.consumption.toFixed(1)}
                  </text>
                </g>
              ))}
            </g>
          ))}
        </svg>
      ) : (
        <p className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">No speed/consumption data available.</p>
      )}
      {expected.length > 0 && !curve.available && (
        <p role="status" className="text-center text-xs text-gray-500 dark:text-gray-400">
          Expected curve unavailable for {condition}; showing available expected points.
        </p>
      )}
      {points.length > 0 && (!expected.length || !comparison.length) && (
        <p role="status" className="text-center text-xs text-gray-500 dark:text-gray-400">
          {!expected.length ? "Expected Performance" : comparisonLabel} data unavailable for {condition}.
        </p>
      )}
    </article>
  );
};

export default VesselCardPerformanceChart;
